import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, pcs, phrase, sizeOf, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { root: string; deg: number; chop: readonly string[]; pad: readonly string[] };

const CHORDS: Readonly<Record<string, Chord>> = {
  Am: { root: 'A1', deg: 0, chop: ['A3', 'C4', 'E4'], pad: ['E3', 'G3', 'C4', 'E4'] },
  C: { root: 'C2', deg: 2, chop: ['G3', 'C4', 'E4'], pad: ['E3', 'G3', 'B3', 'D4'] },
  Dm: { root: 'D2', deg: 3, chop: ['A3', 'D4', 'F4'], pad: ['F3', 'A3', 'C4', 'E4'] },
  Em: { root: 'E2', deg: 4, chop: ['G3', 'B3', 'E4'], pad: ['D3', 'G3', 'B3', 'E4'] },
  F: { root: 'F1', deg: 5, chop: ['A3', 'C4', 'F4'], pad: ['E3', 'A3', 'C4', 'G4'] },
  G: { root: 'G1', deg: 6, chop: ['G3', 'B3', 'D4'], pad: ['D3', 'G3', 'B3', 'E4'] },
};

const PROGS = [
  ['Am', 'Am', 'Dm', 'Dm'],
  ['Am', 'G', 'F', 'G'],
  ['Am', 'Dm', 'Am', 'Em'],
  ['Dm', 'Dm', 'Am', 'Am'],
  ['Am', 'C', 'G', 'Dm'],
] as const;

type Riff = readonly (readonly [step: number, deg: number, len: number, vel?: number])[];

const RIFFS: readonly Riff[] = [
  [[0, 0, 5], [6, 0, 2, 0.8], [8, 4, 3], [11, 2, 2, 0.85], [14, -1, 2, 0.8]],
  [[0, 0, 3], [3, 0, 1, 0.75], [4, 2, 2], [6, 4, 5], [12, 2, 2, 0.85], [14, 1, 2, 0.8]],
  [[2, 0, 4], [6, 0, 2, 0.8], [8, 4, 4], [12, 6, 2, 0.8], [14, 4, 2]],
  [[0, 0, 4], [4, 7, 2, 0.8], [6, 4, 2], [8, 2, 5], [14, 1, 2, 0.75]],
];

const HOOK: Note[][] = [
  [[0, 'E5', 4], [4, 'D5', 2], [6, 'C5', 2], [8, 'A4', 6]],
  [[2, 'C5', 2], [4, 'D5', 2], [6, 'E5', 4], [12, 'G5', 4]],
  [[0, 'F5', 4], [4, 'E5', 2], [6, 'D5', 2], [8, 'C5', 4], [12, 'D5', 4]],
  [[0, 'A5', 6], [8, 'G5', 2], [10, 'E5', 2], [12, 'D5', 4]],
];

const MINOR = [0, 2, 3, 5, 7, 8, 10] as const;
const AEOLIAN = [0, 2, 4, 5, 7, 9, 11] as const;
const DRIPS = ['A5', 'C6', 'D6', 'E6', 'G6', 'A6'] as const;
const DRIP_STEPS = [2, 3, 5, 6, 7, 10, 11, 13, 14, 15] as const;
const KEYS = [0, 2, 3, -2, 1, -1, 4] as const;
const ONE = '........X.......';
const ROCK = 'X.......X.......';
const STEP = 'X...X...X...X...';
const GHOST = ['......o.......o.', '..o.......o..o..', '.......o.o....o.'] as const;
const HATS = ['x-x-x-x-x-x-x-x-', 'x.x.x.x.x.x.x.x.', 'x-xox-xox-xox-xo'] as const;
const OPEN = '..x...x...x...x.';
const BUBBLE = '..x-..x-..x-..x-';

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'A', bars: 8, min: 4, rank: 2 },
    { name: 'rise', bars: 4, min: 4, rank: 3 },
    { name: 'drop', bars: 8, min: 4, rank: 1 },
    { name: 'break', bars: 4, min: 4, rank: 5, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 4 },
  ],
};

const degree = (c: Chord, d: number): string => {
  const at = c.deg + d;
  return transpose(c.root, MINOR[cycle(at, 7)]! + 12 * Math.floor(at / 7) - MINOR[c.deg]!);
};

function score(seed: string, p: Params): Score {
  const ch = chooser(DUB, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const ra = ch.orig ? 0 : ch.rng.int(0, RIFFS.length);
  const riffA = RIFFS[ra]!;
  const riffB = RIFFS[(ra + 1 + (ch.orig ? 0 : ch.rng.int(0, RIFFS.length - 1))) % RIFFS.length]!;
  const ghost = ch.pick(GHOST);
  const hatP = ch.pick(HATS);
  const hook = ch.orig
    ? HOOK
    : phrase(
        ch.rng,
        HOOK.map(cellsOf),
        prog.map((c) => [{ at: 0, tones: pcs([c.root, ...c.pad]) }]),
        AEOLIAN,
        [64, 81],
        72,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 58, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const brk = find(form, 'break');
  const rise = find(form, 'rise');
  const rng = createRng(`dub-${seed}`);
  const human = (v: number) => Math.min(1, v * (0.93 + rng.float() * 0.14));

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number) => events.push({ v, t: g.at(bar, step), bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number, strum = 0) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes, strum });
  const riff = (r: Riff, c: Chord, bar: number, vel: number) => {
    for (const [step, d, len, v] of r) play('sub', bar, step, tr(degree(c, d)), len * s * 0.92, human((v ?? 0.95) * vel));
  };
  const drips = (bar: number, n: number) => {
    const used = new Set<number>();
    for (let k = 0; k < n; k++) {
      const step = DRIP_STEPS[rng.int(0, DRIP_STEPS.length)]!;
      if (used.has(step)) continue;
      used.add(step);
      play('drip', bar, step, tr(DRIPS[rng.int(0, DRIPS.length)]!), s * 2, 0.45 + rng.float() * 0.3);
    }
  };

  const autos: Score['autos'] = [{ v: 'padLp', points: [[g.at(0), 420], [g.at(intro), 1500]] }];
  if (rise !== undefined) {
    const n = sizeOf(form, 'rise');
    autos.push({ v: 'padLp', points: [[g.at(rise), 1500], [g.at(drop1) - s, 3400], [g.at(drop1), 1700]] });
    autos.push({ v: 'keysLp', points: [[g.at(rise), 5000], [g.at(rise + 1), 520], [g.at(drop1) - s, 900], [g.at(drop1), 6000]] });
    autos.push({ v: 'echoLp', points: [[g.at(rise), 2400], [g.at(rise + n / 2), 900], [g.at(drop1) - s, 3600], [g.at(drop1), 2400]] });
  } else {
    autos.push({ v: 'padLp', points: [[g.at(drop1), 1700], [g.at(drop1 + 1), 1700]] });
  }
  if (brk !== undefined) {
    const n = sizeOf(form, 'break');
    autos.push({ v: 'padLp', points: [[g.at(brk), 900], [g.at(brk + n), 2600], [g.at(brk + n) + s, 1700]] });
    autos.push({ v: 'keysLp', points: [[g.at(brk), 1100], [g.at(brk + n) - s, 4200], [g.at(brk + n), 6000]] });
    autos.push({ v: 'echoLp', points: [[g.at(brk), 1600], [g.at(brk + n) - s, 3200], [g.at(brk + n), 2400]] });
  }
  autos.push({ v: 'padLp', points: [[g.at(total - 2), 1500], [g.at(total), 380]] });

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 4);
    const end = b.bar === total - 1;
    const c = end ? prog[0]! : prog[i]!;
    const pad = c.pad.map(tr);
    const chop = c.chop.map(tr);
    const drop = b.name === 'drop' || b.name === 'drop2';
    const throwBar = i === 3;

    if (end) {
      chord('pad', b.bar, 0, pad, g.barLength * 1.6, 0.8, 0.03);
      play('sub', b.bar, 0, tr(c.root), g.barLength * 0.85, 0.9);
      hit('kick', b.bar, 8, 0.85);
      hit('rimx', b.bar, 8, 0.85);
      chord('chopx', b.bar, 12, chop, s * 1.2, 0.7);
      play('drip', b.bar, 14, tr(DRIPS[5]), s * 2, 0.6);
      continue;
    }

    if (b.name === 'intro') {
      chord('pad', b.bar, 0, pad, g.barLength * 0.98, 0.6 + 0.2 * (b.index / b.bars), 0.04);
      drips(b.bar, 2);
      if (b.index === 0) hit('splash', b.bar, 0, 0.7);
      if (b.index >= b.bars / 2) {
        play('sub', b.bar, 0, tr(c.root), s * 7, 0.6);
        play('sub', b.bar, 10, tr(degree(c, 4)), s * 5, 0.5);
      }
      if (b.index >= b.bars / 2) {
        hit('kick', b.bar, 8, 0.8);
        hit('rim', b.bar, 8, 0.75);
        for (const h of hits(HATS[0])) hit('hat', b.bar, h.step, human(h.vel * 0.6));
        chord('chopx', b.bar, 12, chop, s * 1.2, 0.55);
      }
      continue;
    }

    if (b.name === 'rise') {
      chord('pad', b.bar, 0, pad, g.barLength * 0.99, 0.7 + 0.25 * (b.index / Math.max(1, b.bars - 1)), 0.05);
      if (b.index === 0) {
        for (const h of hits(ROCK)) hit('kick', b.bar, h.step, human(h.vel));
        hit('rim', b.bar, 8, 0.9);
        hit('rimx', b.bar, 14, 0.85);
        chord('chop', b.bar, 4, chop, s * 1.1, 0.8);
        chord('chopx', b.bar, 12, chop, s * 1.2, 0.85);
        hit('splash', b.bar, 8, 0.6);
        riff(riffA, c, b.bar, 0.95);
        drips(b.bar, 1);
        continue;
      }
      const lastRise = b.index === b.bars - 1;
      play('sub', b.bar, 0, tr(c.root), (lastRise ? 11 : 13) * s, 0.92);
      if (!lastRise) play('sub', b.bar, 13, tr(degree(c, 4)), s * 2.6, 0.78);
      drips(b.bar, 3);
      if (b.index === 1) play('siren', b.bar, 4, tr('E4'), g.barLength * 0.8, 0.6);
      continue;
    }

    if (b.name === 'break') {
      chord('pad', b.bar, 0, pad, g.barLength * 0.99, 0.82, 0.04);
      hit('kick', b.bar, 8, 0.8);
      hit('rim', b.bar, 8, 0.8);
      chord('chopx', b.bar, 12, chop, s * 1.2, 0.6);
      if (b.index < b.bars - 1) riff(riffA, c, b.bar, 0.8);
      else play('sub', b.bar, 0, tr(c.root), s * 10, 0.85);
      drips(b.bar, 3);
      if (b.last) for (const h of hits('........x.x.xxxx')) hit('rimx', b.bar, h.step, 0.35 + (0.5 * h.step) / 16);
      continue;
    }

    chord('pad', b.bar, 0, pad, g.barLength * 0.98, drop ? 0.62 : 0.72, 0.03);
    for (const h of hits(drop ? STEP : b.name === 'outro' && b.index >= b.bars / 2 ? ONE : ROCK)) hit('kick', b.bar, h.step, human(h.vel));
    hit('rim', b.bar, 8, human(drop ? 0.95 : 0.85));
    for (const h of hits(ghost)) hit('rim', b.bar, h.step, human(h.vel * (drop ? 0.8 : 0.65)));
    for (const h of hits(drop ? hatP : HATS[0])) hit('hat', b.bar, h.step, human(h.vel * 0.8));
    if (drop && e >= 0.7) for (const h of hits(OPEN)) hit('open', b.bar, h.step, human(0.65));
    chord('chop', b.bar, 4, chop, s * 1.1, human(drop ? 0.85 : 0.78));
    chord(throwBar ? 'chopx' : 'chop', b.bar, 12, chop, s * 1.1, human(drop ? 0.85 : 0.78));
    if (throwBar) hit('rimx', b.bar, 14, 0.8);
    if (drop && e >= 0.6) for (const h of hits(BUBBLE)) chord('bubble', b.bar, h.step, chop.map((n) => transpose(n, -12)), s * 0.8, human(h.vel * 0.7));
    riff(drop ? riffB : riffA, c, b.bar, b.name === 'outro' ? 0.85 : 1);
    drips(b.bar, drop ? (e >= 0.7 ? 2 : 1) : b.name === 'outro' ? 2 : 1);

    if (drop) {
      const twice = (b.name === 'drop' && e >= 0.85) || (b.name === 'drop2' && e >= 0.6);
      for (const [step, note, len, v] of hook[i]!) {
        play('lead', b.bar, step, tr(note), len * s * 0.95, human((v ?? 0.9) * 0.95));
        if (twice) play('lead', b.bar, step, transpose(tr(note), 12), len * s * 0.9, (v ?? 0.9) * 0.4);
      }
    }
  }

  if (brk !== undefined && drop2 !== undefined) {
    for (const k of [0, 1]) {
      for (const [step, note, len, v] of hook[cycle(brk + k - intro, 4)]!) play('lead', brk + k, step, tr(note), len * s * 0.95, (v ?? 0.9) * 0.7);
    }
    const from = brk + sizeOf(form, 'break') / 2;
    events.push({ v: 'rise', t: g.at(from), end: g.at(drop2) - s * 2, bar: from, step: 0, vel: 1 });
    events.push({ v: 'fall', t: g.at(brk), end: g.at(brk + 1), bar: brk, step: 0, vel: 1 });
  }
  const riseFrom = rise !== undefined ? rise + Math.max(1, sizeOf(form, 'rise') - 2) : Math.max(intro, drop1 - 2);
  events.push({ v: 'rise', t: g.at(riseFrom), end: g.at(drop1) - s * 2, bar: riseFrom, step: 0, vel: 1 });
  if (rise !== undefined && sizeOf(form, 'rise') >= 3) {
    const at = drop1 - 2;
    events.push({ v: 'siren', t: g.at(at), bar: at, step: 0, vel: 0.7, dur: g.barLength * 2 - s * 2, note: tr('A3'), glide: { to: tr('A5'), at: 0, time: g.barLength * 2 - s * 2 } });
  }
  for (const bar of [drop1, drop2]) {
    if (bar === undefined) continue;
    hit('impact', bar, 0, 1);
    hit('splash', bar, 0, 0.9);
  }

  return {
    id: trackId(DUB, seed, p),
    template: DUB.id,
    seed,
    params: p,
    title: ch.orig ? DUB.ref.title : titleOf(seed),
    genre: DUB.genre,
    bpm: p.bpm,
    swing: 58,
    lufs: -12,
    key,
    form,
    mix: { reverbDecay: 3.2, delayTime: (60 / p.bpm) * 0.5, delayFeedback: 0.3, drive: 1.1, softness: 1.3, masterLowpass: 11000, pumpDepth: 0.12, pumpRelease: 0.3 },
    events,
    autos,
    accents: { kick: 'kick', rim: 'snare', chop: 'stab', lead: 'melody', impact: 'drop' },
  };
}

export const DUB: Template = {
  id: 'dub',
  genre: 'underwater dub / steppers',
  bpm: [110, 126],
  ref: { seed: 'mare-mansa', title: 'Maré mansa', params: { bpm: 116, bars: 28, energy: 0.55 } },
  score,
};
